/**
 * Accès à l'API du module Classement, et suivi des passes de classement.
 *
 * Aucune donnée n'est fabriquée ici : tout vient du serveur. Les erreurs
 * portent le message du serveur (`ErreurApi`), que les vues affichent tel quel.
 */

import { entier, horodatageFr, type OrigineSousPoste } from '@scc/core';
import { ErreurApi } from '../../api.js';

async function echec(r: Response): Promise<never> {
  const corps = (await r.json().catch(() => ({}))) as { erreur?: string };
  throw new ErreurApi(corps.erreur ?? `Erreur ${r.status}`, r.status);
}

async function lire<T>(chemin: string): Promise<T> {
  const r = await fetch(chemin, { headers: { accept: 'application/json' }, credentials: 'same-origin' });
  if (!r.ok) await echec(r);
  return (await r.json()) as T;
}

async function envoyer<T>(methode: string, chemin: string, corps?: unknown): Promise<T> {
  const r = await fetch(chemin, {
    method: methode,
    // Fastify refuse un `content-type` JSON sans corps (relancer, supprimer).
    ...(corps === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(corps) }),
    credentials: 'same-origin',
  });
  if (!r.ok) await echec(r);
  return (await r.json()) as T;
}

export type Statut = 'auto' | 'a_verifier' | 'arbitre' | 'non_classe';
export type Sens = 'recette' | 'depense' | 'tous';
export type SensEcriture = 'recette' | 'depense';
export type ChampRegle = 'tiers' | 'libelle' | 'compte' | 'compte_libelle' | 'type_transaction';
export type OperateurRegle = 'egal' | 'contient' | 'commence_par';

export interface SousPoste {
  readonly id: string;
  readonly poste: string;
  readonly libelle: string;
  readonly description: string;
  readonly sens: Sens;
  readonly ordre: number;
  readonly archive: boolean;
  /** Provenance : compte du plan comptable Zoho, générique d'un poste, création à l'écran ou amorce. */
  readonly origine: OrigineSousPoste;
  /** Code du compte Zoho ; vide hors compte. */
  readonly code: string;
  /** Identifiant du compte dans Zoho ; vide hors compte. */
  readonly zohoAccountId: string;
  /** `account_type` Zoho ; `null` hors compte. */
  readonly typeZoho: string | null;
  /** Sous-poste du compte parent, `null` à la racine. */
  readonly parentId: string | null;
  /** Description du compte dans Zoho, rappelée sous la description saisie. */
  readonly descriptionZoho: string | null;
  /** Faux : le poste est proposé par la synchronisation et attend sa confirmation. */
  readonly posteConfirme: boolean;
}

export interface EcritureClassee {
  readonly transactionId: string;
  readonly date: string;
  readonly montant: number;
  readonly sens: SensEcriture;
  readonly libelle: string;
  readonly tiers: string;
  readonly compteLibelle: string;
  readonly exerciceAffectation: number | null;
  readonly sousPoste: string | null;
  readonly sousPosteLibelle: string | null;
  /** Code du compte Zoho du sous-poste retenu (vide hors compte). */
  readonly sousPosteCode: string | null;
  readonly sousPostePoste: string | null;
  readonly sousPosteOrigine: OrigineSousPoste | null;
  readonly exercice: number | null;
  readonly source: string;
  readonly statut: Statut;
  readonly confiance: number | null;
  readonly alternatives: readonly { readonly sousPoste: string; readonly probabilite: number }[];
}

export interface Suggestion {
  readonly champ: ChampRegle;
  readonly operateur: OperateurRegle;
  readonly valeur: string;
}

export interface RegleServie {
  readonly id: number;
  readonly priorite: number;
  readonly champ: ChampRegle;
  readonly operateur: OperateurRegle;
  readonly valeur: string;
  readonly sens: Sens;
  readonly sousPoste: string;
  readonly actif: boolean;
  readonly origine: string;
  readonly creePar: string;
  readonly creeLe: string;
  readonly nbEcritures: number;
}

export interface BilanPasse {
  readonly total: number;
  readonly arbitres: number;
  /** Classées par le compte Zoho de l'écriture. */
  readonly parCompte: number;
  readonly parRegle: number;
  /** Classées par Jev, y compris celles « à vérifier ». */
  readonly parJev: number;
  readonly aVerifier: number;
  readonly nonClasses: number;
  readonly jev: boolean;
}

export interface EtatPasse {
  readonly enCours: boolean;
  readonly jev: boolean;
  readonly debut: string | null;
  readonly fin: string | null;
  readonly dernierBilan: BilanPasse | null;
  readonly erreur: boolean;
}

export interface Resume {
  readonly total: number;
  readonly auto: number;
  readonly aVerifier: number;
  readonly nonClasses: number;
  readonly arbitres: number;
  /** Écritures classées, selon ce qui les a classées. */
  readonly parSource: {
    readonly compte: number;
    readonly regle: number;
    readonly jev: number;
    readonly arbitrage: number;
  };
  readonly passe: EtatPasse;
}

/** Bilan d'une synchronisation du plan comptable Zoho. */
export interface BilanSynchro {
  readonly crees: number;
  readonly misAJour: number;
  readonly archives: number;
  /** Comptes actifs dont le rattachement au poste est encore proposé. */
  readonly aConfirmer: number;
  /** Présent seulement quand la synchronisation a fait la migration unique des classements. */
  readonly migration?: {
    readonly arbitragesPrecises: number;
    readonly arbitragesReportes: number;
    readonly remisAClasser: number;
    readonly reglesReportees: number;
  };
}

/** Synchronisation refusée par le serveur (plan vide ou tronqué) : rien n'a été écrit. */
export interface SynchroRefusee {
  readonly refuse: true;
  readonly motif: string;
}

export interface RegleSaisie {
  readonly champ: ChampRegle;
  readonly operateur: OperateurRegle;
  readonly valeur: string;
  readonly sens: Sens;
  readonly sousPoste: string;
}

export interface ResultatTest {
  readonly nb: number;
  readonly exemples: readonly {
    readonly transactionId: string;
    readonly date: string;
    readonly montant: number;
    readonly libelle: string;
    readonly tiers: string;
  }[];
  readonly conflits: readonly {
    readonly transactionId: string;
    readonly libelle: string;
    readonly sousPoste: string | null;
  }[];
}

export const resume = (): Promise<Resume> => lire<Resume>('/api/classement/resume');

export const ecrituresClassees = (statut?: string, source?: string): Promise<{ ecritures: EcritureClassee[] }> => {
  const p = new URLSearchParams();
  if (statut) p.set('statut', statut);
  if (source) p.set('source', source);
  const q = p.toString();
  return lire<{ ecritures: EcritureClassee[] }>(`/api/classement/ecritures${q === '' ? '' : `?${q}`}`);
};

export const listeSousPostes = (): Promise<{ sousPostes: SousPoste[] }> =>
  lire<{ sousPostes: SousPoste[] }>('/api/classement/sous-postes');

export const listeRegles = (): Promise<{ regles: RegleServie[] }> =>
  lire<{ regles: RegleServie[] }>('/api/classement/regles');

/**
 * `exercice` n'est envoyé que s'il change quelque chose : omis, l'arbitrage
 * conserve la surcharge existante ; `null` la retire.
 */
export const arbitrer = (
  transactionId: string,
  sousPoste: string,
  exercice?: number | null,
): Promise<{ arbitre: boolean; suggestion: Suggestion | null; sens: SensEcriture }> =>
  envoyer('POST', '/api/classement/arbitrer', {
    transactionId,
    sousPoste,
    ...(exercice !== undefined ? { exercice } : {}),
  });

export const validerLot = (seuil: number, statut: 'a_verifier' | 'auto'): Promise<{ valides: number }> =>
  envoyer('POST', '/api/classement/valider-lot', { seuil, statut });

/** Lance une passe avec Jev en arrière-plan (202) ; 409 si une passe avec Jev est déjà en cours. */
export const relancer = (): Promise<{ lancee: boolean; etat: EtatPasse }> =>
  envoyer('POST', '/api/classement/relancer');

export const testerRegle = (r: RegleSaisie): Promise<ResultatTest> =>
  envoyer('POST', '/api/classement/regles/tester', r);

export const creerRegle = (r: RegleSaisie & { origine: 'arbitrage' | 'manuelle' }): Promise<{ id: number }> =>
  envoyer('POST', '/api/classement/regles', r);

export const modifierRegle = (
  id: number,
  m: Partial<RegleSaisie> & { priorite?: number; actif?: boolean },
): Promise<{ id: number }> => envoyer('PUT', `/api/classement/regles/${String(id)}`, m);

export const supprimerRegle = (id: number): Promise<{ efface: boolean }> =>
  envoyer('DELETE', `/api/classement/regles/${String(id)}`);

export const creerSousPoste = (
  s: Pick<SousPoste, 'id' | 'poste' | 'libelle' | 'description' | 'sens'>,
): Promise<{ id: string }> => envoyer('POST', '/api/classement/sous-postes', s);

export const modifierSousPoste = (
  id: string,
  m: { libelle?: string; description?: string; archive?: boolean; poste?: string; confirmer?: true },
): Promise<{ id: string }> => envoyer('PUT', `/api/classement/sous-postes/${encodeURIComponent(id)}`, m);

/**
 * Relit le plan comptable dans Zoho et synchronise les sous-postes ; le serveur
 * lance ensuite une passe par les règles (à suivre comme les autres). Un plan
 * qui paraît tronqué est refusé (409) sans rien écrire : la réponse porte alors
 * le motif, et `force` permet de passer outre.
 */
export async function synchroniserPlanComptable(force = false): Promise<BilanSynchro | SynchroRefusee> {
  const r = await fetch('/api/classement/plan-comptable/synchroniser', {
    method: 'POST',
    ...(force ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify({ force: true }) } : {}),
    credentials: 'same-origin',
  });
  if (r.status === 409) {
    const corps = (await r.json().catch(() => ({}))) as { motif?: string };
    return { refuse: true, motif: corps.motif ?? 'La synchronisation a été refusée : le plan comptable reçu paraît incomplet.' };
  }
  if (!r.ok) await echec(r);
  return (await r.json()) as BilanSynchro;
}

// ── Suivi des passes ────────────────────────────────────────────────────────

/** Délai entre deux lectures du résumé pendant qu'une passe tourne. */
const DELAI_SUIVI_MS = 5000;
/** Lectures du résumé qui peuvent échouer d'affilée avant d'abandonner le suivi. */
const ERREURS_TOLEREES = 3;
/** Au-delà, le suivi s'arrête : la passe continue côté serveur, mais on n'attend plus. */
const DUREE_MAX_SUIVI_MS = 15 * 60 * 1000;

const abonnes = new Set<() => void>();
let suivi: Promise<Resume> | null = null;

/**
 * Appelée à chaque fin de passe suivie : les vues rechargent ainsi leurs
 * données, quelle que soit la vue qui a déclenché la passe. Renvoie la
 * fonction qui retire l'abonnement.
 */
export function surFinDePasse(rappel: () => void): () => void {
  abonnes.add(rappel);
  return () => {
    abonnes.delete(rappel);
  };
}

/**
 * Prévient les vues abonnées qu'une donnée du module a changé (fin de passe,
 * sous-poste créé ou renommé). `origine` est le rappel de la vue qui a causé
 * le changement : elle a déjà mis son affichage à jour et n'est pas prévenue.
 */
export function notifierChangement(origine?: () => void): void {
  for (const rappel of [...abonnes]) if (rappel !== origine) rappel();
}

/**
 * Attend la fin de la passe en cours en lisant le résumé toutes les ~5 s, puis
 * prévient les vues abonnées. Un seul suivi tourne à la fois : les appels
 * concurrents partagent la même attente. Rejette si le résumé est illisible.
 */
export function attendreFinDePasse(): Promise<Resume> {
  if (suivi) return suivi;
  const attente = (async (): Promise<Resume> => {
    try {
      // Première lecture rapide : une passe par les seules règles est courte.
      let delai = 1000;
      let erreurs = 0;
      const debut = Date.now();
      for (;;) {
        await new Promise<void>((fin) => {
          setTimeout(fin, delai);
        });
        delai = DELAI_SUIVI_MS;
        try {
          const r = await resume();
          erreurs = 0;
          if (!r.passe.enCours) return r;
        } catch (e) {
          // Une coupure brève du réseau ne doit pas interrompre le suivi.
          erreurs += 1;
          if (erreurs >= ERREURS_TOLEREES) {
            throw e instanceof ErreurApi ? e : new ErreurApi('Le suivi du classement a été interrompu.', 0);
          }
        }
        if (Date.now() - debut > DUREE_MAX_SUIVI_MS) {
          throw new ErreurApi(
            'Le classement dure plus longtemps que prévu ; il se poursuit sur le serveur. Rechargez la page pour en voir l’état.',
            0,
          );
        }
      }
    } finally {
      suivi = null;
    }
  })();
  suivi = attente;
  void attente.then(
    () => {
      notifierChangement();
    },
    () => undefined,
  );
  return attente;
}

/** Phrase de bilan de la dernière passe terminée, ou `null` si aucune n'a eu lieu. */
export function phraseBilan(p: EtatPasse): string | null {
  if (p.erreur) return 'Le dernier classement a échoué ; les classements précédents sont conservés.';
  const b = p.dernierBilan;
  if (!b) return null;
  return (
    `Dernier classement${p.fin ? ` (${horodatageFr(p.fin)})` : ''} : ${entier(b.total)} écritures — ` +
    `${entier(b.parCompte)} par compte, ${entier(b.parRegle)} par règle, ${entier(b.parJev)} par Jev dont ${entier(b.aVerifier)} à vérifier, ` +
    `${entier(b.arbitres)} arbitrées, ${entier(b.nonClasses)} non classées.`
  );
}
